/**
 * The reader — §6, §7.
 *
 * THIS COMPONENT HOLDS NO STATE AND NO EFFECTS OF ITS OWN. It reads the page from the
 * URL, asks `usePageScope` what that page means, and lays the shell out. Everything
 * else lives somewhere with a name:
 *
 *   preferences        → `stores/settings/`      (persisted)
 *   session state      → `stores/reader.ts`      (§7.4, never persisted)
 *   what a control does → `lib/commands.ts`      (the one place)
 *   what the page means → `lib/usePageScope.ts`  (§7.4 derivation)
 *   listeners + visits  → `lib/useReaderShell.ts`
 *
 * §7.4 IS STILL THE LOAD-BEARING RULE: page-scoped state is DERIVED against the page on
 * screen, never cleared by whoever navigated. There are six ways the page can change
 * and resetting selection inside any one of them leaves the other five stale.
 */
import { useCallback, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { TOTAL_PAGES, chaptersOnPage, clampPage, juzOfPage } from '@mushaf/corpus'
import { metaQuery, pageQuery, useMeta, useMushafPage } from '../lib/queries'
import {
  closeSearch,
  goToPage,
  openSearchHit,
  openStudy,
  tapAyah,
  toggleRepeatBar,
  toggleSearch,
} from '../lib/commands'
import { usePageScope } from '../lib/usePageScope'
import { useReaderKeyboard, useRecordVisit } from '../lib/useReaderShell'
import { useSheetInset } from '../lib/useSheetInset'
import { useSwipePage } from '../lib/useSwipePage'
import { useSettings } from '../stores/settings'
import { usePlayer, selectCurrentKey } from '../stores/player'
import { useReader } from '../stores/reader'
import { useQueueTrimOnAutoAdvanceOff } from '../audio/useQueueGrowth'
import { TopBar } from '../components/reader/TopBar'
import { AudioBar } from '../components/reader/AudioBar'
import { RepeatBar } from '../components/reader/RepeatBar'
import { MushafPageView, firstAyahNumberOnPage } from '../components/reader/MushafPageView'
import { chapterLabel } from '../content/chapters'
import { useMeasure } from '../components/reader/useMeasure'
import { ReaderPanel } from '../components/panel/ReaderPanel'
import { SearchPanel } from '../components/search/SearchPanel'
import { useIsDesktop } from '../lib/useMediaQuery'

export const Route = createFileRoute('/read/$page')({
  // Warm meta and the page JSON in the loader so the first paint has data.
  loader: async ({ context, params }) => {
    const meta = await context.queryClient.ensureQueryData(metaQuery)
    const withV4 = useSettings.getState().mode === 'tajweed'
    // CLAMP RATHER THAN THROW — a bad URL should still render a page (§6).
    void context.queryClient.ensureQueryData(
      pageQuery(clampPage(Number(params.page)), meta, withV4),
    )
    return null
  },
  component: Reader,
})

function Reader() {
  // The URL is the OWNER of "which page is on screen" (§4.1). It is clamped here
  // rather than validated in the route, so a hand-typed /read/9999 still renders.
  const { page: rawPage } = Route.useParams()
  const page = clampPage(Number(rawPage))

  const { data: meta } = useMeta()
  const mode = useSettings((s) => s.mode)
  const fontScale = useSettings((s) => s.fontScale)
  const repeatBar = useSettings((s) => s.repeatBar)

  const { data: loaded } = useMushafPage(page, mode === 'tajweed')
  const playingKey = usePlayer(selectCurrentKey)

  const panel = useReader((s) => s.panel)
  const markingMode = useReader((s) => s.markingMode)

  /**
   * Below `lg` the panel is a strip under the page, not a column beside it, and the
   * decision has to be made HERE rather than inside the slot: a collapsed strip renders
   * a DIFFERENT panel (the two reading lines only), not the same one clipped.
   */
  const isDesktop = useIsDesktop()
  const panelExpanded = useSettings((s) => s.panelExpanded)
  const sheetRef = useRef<HTMLDivElement | null>(null)

  const scope = usePageScope(page, meta)

  useRecordVisit(page)
  useReaderKeyboard(page)
  useQueueTrimOnAutoAdvanceOff(meta)

  // --- measurement (§7.6) --------------------------------------------------
  const { stageRef, cardRef, bodyRef, fit, cardWidth, justified } = useMeasure({
    pageKey: page,
    mode,
    fontScale,
    v4Ready: loaded?.v4Ready ?? false,
    ready: Boolean(loaded),
  })

  /**
   * The sheet floats OVER the page, so the page needs scroll room behind it or its last
   * line is unreachable. Padding, not height — see `useSheetInset`.
   */
  useSheetInset(sheetRef, stageRef, { active: !isDesktop, expanded: panelExpanded })

  /**
   * Swipe to turn the page. `useCallback` is not cosmetic: the hook subscribes in an
   * effect keyed on these two, so fresh closures every render would detach and reattach
   * three listeners on every store update.
   *
   * Clamped the same way the stepper's arrows are, and for the same reason (T65): at 1
   * and 604 the gesture is a harmless no-op rather than a dead zone.
   */
  const swipeForward = useCallback(() => goToPage(clampPage(page + 1)), [page])
  const swipeBack = useCallback(() => goToPage(clampPage(page - 1)), [page])
  useSwipePage(stageRef, { onForward: swipeForward, onBack: swipeBack })

  const chapters = meta ? chaptersOnPage(meta, page) : []

  /**
   * ── THE PANEL COLUMN IS ALWAYS RESERVED ────────────────────────────────────────
   *
   * The parent app reserved it keyed on what was actually rendered, because the panel
   * could close and leaving the column behind wasted a third of the reader. Here the
   * panel never closes, so the column is unconditional — and that is what stops the
   * mushaf from resizing (and re-measuring, §7.6) every time the panel's content
   * changes.
   *
   * Search still borrows the column rather than getting one of its own: two 340 px
   * columns beside the page leaves the page nothing.
   */
  const panelContent =
    panel === 'search' ? (
      <SearchPanel onOpenHit={openSearchHit} onClose={closeSearch} />
    ) : scope.panelSubject ? (
      <ReaderPanel
        meta={meta}
        page={page}
        verseKey={scope.panelSubject}
        ayahKeys={scope.ayahKeys}
        onSelect={openStudy}
        sheet={!isDesktop}
        expanded={panelExpanded}
      />
    ) : null

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TopBar
        meta={meta}
        page={page}
        juz={meta ? juzOfPage(meta, page) : 1}
        surahName={chapters[0] ? chapterLabel(chapters[0]) : '—'}
        searchOpen={panel === 'search'}
        repeatBar={repeatBar}
        onToggleSearch={toggleSearch}
        onToggleRepeatBar={toggleRepeatBar}
        onNavigate={goToPage}
        // The same command a search hit uses, so a verse reached from the picker and a
        // verse reached from search arrive in identical state (§7.4).
        onOpenVerse={openSearchHit}
      />

      {/**
       * THE PANEL IS THE FIRST COLUMN, so it is on the LEFT under an LTR root. The parent
       * app's panel was also visually on the left — but because it was the SECOND column
       * of an RTL grid. Same side, opposite source order.
       *
       * ── Two equal halves, and why the columns are fractions ────────────────────
       *
       * The panel is sized to the SOLVED CARD WIDTH so the two blocks are literally the
       * same width, and each is pushed to the inner edge of its half — so they meet as a
       * matched pair at the centre of the screen, at any viewport width, with no
       * max-width to pick.
       *
       * The COLUMNS stay fractions on purpose. `grid-cols-2` makes the stage exactly half
       * the row whatever it contains, which is what keeps this safe: the solver reads
       * `stage.clientWidth` (L4), so a stage that sized itself to its own card would be
       * the two controllers feeding each other — card narrows, stage narrows, card
       * narrows again. Sizing only the PANEL from `cardWidth` cannot loop, because the
       * panel is not what the observer watches.
       */}
      {/**
       * `relative` and a reserved bottom padding: THE PAGE KEEPS ONE HEIGHT.
       *
       * The strip used to be in the flow, so opening it shrank the stage from 500px to
       * 134px — the mushaf collapsed to three lines and the measurement solver re-solved
       * the whole card for a geometry that lasts as long as the disclosure is open.
       *
       * It is now a pure OVERLAY: it reserves no layout space at all. Reserving it as
       * padding here was the previous attempt, and it left a band of dead space that did
       * not even match the strip (136px reserved against a 176px strip). The page runs
       * full height and `useSheetInset` gives it scroll room behind the sheet instead.
       */}
      <div
        className={`relative grid min-h-0 flex-1 gap-3 p-3 ${
          panelContent && isDesktop ? 'lg:grid-cols-2' : ''
        }`}
      >
        {panelContent && isDesktop && (
          // `ms-auto` pushes it to the inner edge of its half, where the page meets it.
          <div
            className="ms-auto min-h-0 w-full"
            style={{ maxWidth: cardWidth !== null ? `${cardWidth}px` : undefined }}
          >
            {panelContent}
          </div>
        )}

        <div ref={stageRef} className={`mushaf-stage ${panelContent ? 'is-paired' : ''}`}>
          <div
            ref={cardRef}
            className="mushaf-card"
            style={{
              // @ts-expect-error CSS custom properties are not in CSSProperties.
              '--fit': fit,
              '--card-width': cardWidth !== null ? `${cardWidth}px` : undefined,
            }}
          >
            <div className="mushaf-head">
              {/* The page header names the surah in ARABIC, as the printed page does.
                  B6: under an LTR root this needs `lang`/`dir` explicitly — the Arabic
                  app inherited both from <html> and so carries neither. */}
              <span lang="ar" dir="rtl">
                {chapters.map((c) => c.nameArabic).join(' · ')}
              </span>
              <span className="flex-1" />
              <span>
                Page <span dir="ltr">{page}</span>
              </span>
            </div>

            <div
              ref={bodyRef}
              className={`mushaf-body ${markingMode === 'tap' ? 'is-marking' : ''}`}
            >
              {loaded ? (
                <MushafPageView
                  data={loaded.data}
                  mode={mode}
                  v4Ready={loaded.v4Ready}
                  justified={justified}
                  selectedKey={scope.selectedHere}
                  playingKey={playingKey}
                  rangedKeys={scope.rangedKeys}
                  bookmarkedKeys={scope.bookmarkedKeys}
                  onSelectAyah={tapAyah}
                />
              ) : (
                // L1: no page data before its fonts, so there is nothing to paint yet.
                <p className="p-8 text-center text-sm text-[var(--ink-faint)]">Loading…</p>
              )}
            </div>

            <div className="mushaf-foot">
              <span>{page}</span>
            </div>
          </div>
        </div>

        {/**
         * MOBILE: THE SHEET IS ANCHORED TO THE BOTTOM OF THE PAGE AREA.
         *
         * `absolute`, not `fixed` — so it stops at the bottom of the stage's box rather
         * than the viewport's, and the audio bar below it stays reachable. (`fixed
         * bottom-0` put it squarely on top of the transport.)
         *
         * It grows UPWARD over the page. The page does not move.
         */}
        {/**
         * SEARCH IS NOT A SHEET ON A PHONE — it is the whole area.
         *
         * It used to fall through to the sheet below, which produced three separate
         * defects from one cause: `SearchPanel` is built `h-full` with a `flex-1`
         * scrolling result list, and the sheet's height is `auto`, so a percentage height
         * resolved against nothing and the list was never bounded; the panel appeared
         * beneath a disclosure button labelled with a VERSE KEY, which belongs to the
         * translation; and the field `autoFocus`es, so the keyboard opened over a sheet
         * anchored to the bottom of the page area.
         *
         * `inset-0` gives it a definite height, which is all `h-full` ever needed, and
         * searching is a modal task anyway — the panel already carries its own close
         * button. Above `lg` nothing changes: search still borrows the panel column.
         */}
        {panelContent && !isDesktop && panel === 'search' && (
          <div className="absolute inset-0 z-40">{panelContent}</div>
        )}

        {panelContent && !isDesktop && panel !== 'search' && (
          <div
            ref={sheetRef}
            data-panel-sheet
            /**
             * `max-h-full` so a long verse cannot push the sheet off the top of the
             * screen — open on 2:282 it ran 975px tall in a 656px area and the toggle
             * went with it. Past that it scrolls itself.
             *
             * SHUT, the cap is a FIFTH OF THE SCREEN. The strip no longer truncates, so
             * its height is the verse's height, and 2:282 unclamped would cover the page
             * completely — a reader would be looking at a translation with no mushaf
             * behind it. The full text is still all there, reached by scrolling the strip
             * (the header bar is `sticky`, so the way out does not scroll away with it).
             * Nothing is cut off: that is the difference between "the rest is below" and
             * "the rest is gone", which is what was wrong with the `…`.
             *
             * `dvh` and not `%`: a fifth of the SCREEN, not of the grid area, and `vh`
             * would measure the large viewport rather than the one the toolbar leaves
             * (L35).
             *
             * `overscroll-contain`: at the end of its own scroll this used to chain out
             * to the stage and scroll the page behind the sheet being read.
             */
            className={`absolute inset-x-0 bottom-0 z-30 overflow-y-auto overscroll-contain border-t border-[var(--line)] bg-[var(--surface)] ${
              panelExpanded ? 'h-full' : 'max-h-[20dvh]'
            }`}
          >
            {/* The sheet's bar — Means/Says, the verse key and Open — now lives in
                `ReaderPanel`, because the verse header had to go ABOVE it and that
                header is built from the panel's own data. */}
            {panelContent}
          </div>
        )}
      </div>

      {repeatBar && (
        <RepeatBar
          ayahKeys={scope.ayahKeys}
          range={scope.rangeHere}
          passageKeys={scope.passageKeys}
        />
      )}

      <AudioBar meta={meta} page={page} ayahKeys={scope.ayahKeys} />

      <span className="sr-only">
        {loaded ? `First verse on this page: ${firstAyahNumberOnPage(loaded.data)}` : ''}
        {page < TOTAL_PAGES ? '' : ' Last page.'}
      </span>
    </div>
  )
}
